<template>
  <LcTabs
    v-bind="{ tabs, value }"
    adaptive-height
    class="tabs"
    :class="{ 'single-tab': tabs.length <= 1 }"
    :disable-swipe="tabs.length <= 1"
    render-only-visible
    tab-flex-direction="row"
    trigger="manual"
    @update:value="navigateToTab"
  >
    <template v-if="currentRoute.params.id === CUSTOM_PLAY_NOW_ROUTES.Scratchcards.name" #subnav>
      <ScratchcardPriceFilterBar
        :active-prices="activePrices"
        :prices="prices"
        @price:click="handleSetFilter"
      />
    </template>

    <template #tab-panel="{ tab }">
      <component
        :is="tab.data.comp"
        v-bind="{ isScratchcardEnabled }"
        :high-frequency-teasers="highFrequencyResult?.defaultTeasers"
        :loading="lotteriesLoading || scratchcardLoading || highFrequencyLoading"
        :lottery-teasers="lotteriesResult?.defaultTeasers as DefaultTeasersPayload"
        :scratchcard-teasers="filteredScratchcardTeasers"
        @refetch="lotteriesRefetch"
      />
    </template>
  </LcTabs>
</template>

<script lang="ts" setup>
import {
  type DefaultTeasersPayload,
  KnownProductType,
  type ScratchcardTeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import Lotteries from '@/custom-pages/play-now/children/lotteries.vue'
import Scratchcards from '@/custom-pages/play-now/children/scratchcards.vue'
import { CUSTOM_PLAY_NOW_ROUTES } from '~/constants/playNowRoutes'

definePageMeta({
  footer: 'condensed',
  header: 'full',
  key: 'play-now',
  middleware: ['age-verification', 'self-excluded'],
})

type PlayNowTabData = {
  comp: typeof Lotteries | typeof Scratchcards
  path: string
}

const { currentRoute } = useRouter()
const licensedTerritory = useLicensedTerritory({ raw: true })
const { t } = useI18n()

const isScratchcardEnabled = useIsScratchcardEnabled()

const {
  activePrices,
  filteredTeasers: filteredScratchcardTeasers,
  prices,
  setFilter,
  setUp,
} = useScratchcardFilter()

const FETCH_OPTIONS = {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-first',
  pollInterval: 3_000_000,
}

const { activeTabs: tabs } = useTabs<PlayNowTabData>(
  computed(() => [
    {
      data: { comp: Lotteries, path: CUSTOM_PLAY_NOW_ROUTES.Lotteries.path },
      icon: 'tab-lotteries',
      name: 'lotteries',
      title: t('playnow.tab.lotteries.title'),
    },
    {
      active: isScratchcardEnabled,
      data: {
        comp: Scratchcards,
        path: CUSTOM_PLAY_NOW_ROUTES.Scratchcards.path,
      },
      icon: 'tab-scratchcards',
      name: 'scratchcards',
      title: t('playnow.tab.scratchcards.title'),
    },
  ]),
)

const {
  loading: lotteriesLoading,
  refetch: lotteriesRefetch,
  result: lotteriesResult,
} = useDefaultTeasersQuery(
  { licensedTerritory, productType: KnownProductType.Lottery },
  FETCH_OPTIONS,
)

const { loading: scratchcardLoading, onResult } = isScratchcardEnabled
  ? useDefaultTeasersQuery(
      { licensedTerritory, productType: KnownProductType.Scratchcard },
      FETCH_OPTIONS,
    )
  : { loading: false, onResult: () => {} }

const { loading: highFrequencyLoading, result: highFrequencyResult } = useDefaultTeasersQuery(
  { licensedTerritory, productType: KnownProductType.HighFrequencyGame },
  FETCH_OPTIONS,
)

const value = computed(() => String(currentRoute.value.params.id))

const navigateToTab = async (name?: string) => {
  const nextTab = tabs.value.find((tab) => tab.name === name)
  if (!nextTab) return

  await navigateTo(nextTab.data.path, { replace: true })

  if (name === CUSTOM_PLAY_NOW_ROUTES.Scratchcards.name) {
    setUp()
  }
}

const handleSetFilter = (price: number) => {
  scrollTo({ top: 0 })
  setFilter(price)
}

onResult((result) => {
  const teasers = (result?.data?.defaultTeasers?.teasers ?? []) as ScratchcardTeaserPayload[]
  setUp(teasers)
})
</script>

<style lang="scss" scoped>
.single-tab {
  margin-top: token('size/space/gap/none');

  /* stylelint-disable-next-line selector-class-pattern */
  &:deep(.lc-tabs__carousel) {
    overflow: visible;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  &:deep(.lc-carousel__container) {
    overflow: visible;
  }
}

/* stylelint-disable-next-line selector-class-pattern */
:deep(.lc-tabs__nav_container) {
  nav button {
    padding: token('size/space/gap/150') token('size/space/gap/none');
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .lc-tabs__tablist {
    margin-bottom: token('size/space/gap/none');
  }

  /* stylelint-disable-next-line selector-class-pattern */
  &:deep(.lc-carousel__wrap) {
    padding-top: token('size/space/gap/none');
  }
}
</style>
